import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { Checkbox } from '@ui/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getDefaultRowId,
  getGlobalFilterFn,
  handleRowSelectionChange,
} from '../utils/table-helpers';
import { useVisibility } from './use-visiblility';

type UseDefaultTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  isResizable?: boolean;
  hiddenColumns?: string[];
  enableColumnFilter?: boolean;
  disablePagination?: boolean;
  rowSelection?: RowSelectionState;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  getRowId?: (row: TData) => string;
  isTemplate?: boolean;
  defaultSorting?: SortingState;
};

export const useDefaultTable = <TData,>({
  data,
  columns,
  isResizable,
  hiddenColumns,
  enableColumnFilter,
  rowSelection,
  setRowSelection,
  getRowId,
  disablePagination,
  columnPinning,
  isTemplate,
  defaultSorting,
}: UseDefaultTableProps<TData>) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(
    columns.map((column) => column.id!),
  );
  const [columnSorting, setColumnSorting] = useState<SortingState>(
    defaultSorting ?? [],
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const defaultHidden: Record<string, boolean> = useMemo(
    () =>
      hiddenColumns?.reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {},
    [hiddenColumns],
  );

  const [columnVisibility, setColumnVisibility] = useState(defaultHidden);

  const { columnStateSaveId } = useVisibility();

  const tableColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (rowSelection !== undefined && setRowSelection) {
      return [
        {
          id: 'select',
          header: ({ table }: { table: Table<unknown> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllRowsSelected(!!value)
                }
              />
            </div>
          ),
          cell: ({ row }: { row: Row<unknown> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                className="-ml-2"
                disabled={!row.getCanSelect()}
                onCheckedChange={row.getToggleSelectedHandler()}
              />
            </div>
          ),
          size: 40,
          maxSize: 40,
          meta: {
            isServiceColumn: true,
          },
        } as ColumnDef<TData, unknown>,
        ...columns,
      ];
    }

    return columns;
  }, [columns, rowSelection, setRowSelection]);

  const _getRowId = useMemo(() => getRowId || getDefaultRowId, [getRowId]);

  const handleColumnVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVisibility =
          typeof updater === 'function' ? updater(prev) : updater;

        if (columnStateSaveId && !isTemplate) {
          localStorage.setItem(
            columnStateSaveId,
            JSON.stringify(newVisibility),
          );
        }

        return newVisibility;
      });
    },
    [columnStateSaveId],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      columnPinning: {
        left: ['select', ...(columnPinning?.left ?? [])],
        right: ['actions', ...(columnPinning?.right ?? [])],
      },
      ...(disablePagination ? {} : { pagination }),
      columnOrder,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
      sorting: columnSorting,
      columnSizing,
      columnVisibility,
    },
    enableColumnResizing: isResizable,
    enableGlobalFilter: true,
    autoResetPageIndex: false,
    enableColumnFilters: enableColumnFilter,
    columnResizeMode: 'onChange',
    globalFilterFn: getGlobalFilterFn<TData>(),
    filterFromLeafRows: true,
    defaultColumn: {
      filterFn: 'includesString',
      size: 150,
      minSize: 50,
      maxSize: 500,
    },
    onSortingChange: setColumnSorting,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: (updater) =>
      handleRowSelectionChange(updater, rowSelection, setRowSelection),
    onColumnOrderChange: setColumnOrder,
    getRowId: _getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(disablePagination
      ? {}
      : {
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
        }),
  });

  const isEmpty = useMemo(() => {
    return !table.getRowModel().rows.length;
  }, [table.getRowModel().rows]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }, []);

  useEffect(() => {
    if (columnStateSaveId && !isTemplate) {
      const columnState = localStorage.getItem(columnStateSaveId);
      if (columnState) {
        try {
          const savedVisibility = JSON.parse(columnState);
          setColumnVisibility(savedVisibility);
          table.setColumnVisibility(savedVisibility);
        } catch (error) {
          console.warn('Failed to parse saved column visibility state:', error);
        }
      }
    }
  }, [columnStateSaveId]);

  return {
    table,
    tableColumns,
    isEmpty,
    columnOrder,
    handleDragEnd,
    pagination,
    columnSizing,
  };
};
